import { useState, useEffect } from "react";
import { Company, CreateCompanyData } from "@/services/company-service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface CreateEditCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave: (data: CreateCompanyData, id?: string) => Promise<void>;
}

export function CreateEditCompanyDialog({
  open,
  onOpenChange,
  company,
  onSave,
}: CreateEditCompanyDialogProps) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (company) {
      setName(company.name);
    } else {
      clearForm();
    }
  }, [company, open]);

  const clearForm = () => {
    setName("");
    setErrors({});
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Nome é obrigatório";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => document.getElementById(Object.keys(newErrors)[0])?.focus(), 0);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const data: CreateCompanyData = { name: name.trim() };
    try {
      await onSave(data, company?.id);
      onOpenChange(false);
    } catch (error) {
      // Erros de salvamento ficam a cargo do pai
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">
            {company ? "Editar Empresa" : "Nova Empresa"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="filter-label" htmlFor="name">Nome da Empresa *</Label>
            <Input className="filter-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da empresa" aria-invalid={!!errors.name} />
            {errors.name && <p className="text-red-600 text-xs">{errors.name}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t gap-2">
          {Object.keys(errors).length > 0 && (
            <p className="text-red-600 text-xs">Corrija os campos marcados em vermelho.</p>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button size="sm" className="btn-header" onClick={handleSave}>
              {company ? "Salvar Alterações" : "Criar Empresa"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
